/**
 * Loss Factor Admin Service
 *
 * CRUD client for the loss-factor reference tables, used by the Loss Factors
 * admin screen. Writes go through the backend (service role) because the tables'
 * RLS allows reads but not writes. Mirrors sysConfigApiService transport.
 *
 * Backend: server/features/scopingAi/routes/lossFactorRoutes.ts
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const headers = {
  'Content-Type': 'application/json',
  'x-api-key': 'scopingai',
};

export type MlfRow = {
  id: string;
  region: string;
  factor_type: 'load' | 'gen';
  direction: 'import' | 'export' | null;
  location: string | null;
  voltage_kv: string | null;
  tni_code: string | null;
  duid: string | null;
  connection_point_id: string | null;
  current_fy: string;
  current_mlf: number;
  prior_fy: string | null;
  prior_mlf: number | null;
};

export type DlfRow = {
  id: string;
  state: string;
  dnsp: string;
  table_name: string | null;
  network_level: string | null;
  nmi: string | null;
  dlf_code: string;
  current_fy: string;
  current_dlf: number;
  prior_fy: string | null;
  prior_dlf: number | null;
};

export type ListParams = { search?: string; page?: number; limit?: number };
export type ListResponse<T> = { success: boolean; data?: T[]; count?: number; page?: number; limit?: number; error?: string };
export type MutateResponse<T> = { success: boolean; data?: T; error?: string };
/** 'replace' clears the table first; 'append' only inserts. */
export type BulkMode = 'replace' | 'append';

export type BulkUploadResponse = {
  success: boolean;
  mode?: BulkMode;
  /** Rows removed by a replace. */
  deleted?: number;
  total?: number;
  inserted?: number;
  skipped?: number;
  errors?: Array<{ row: number; error: string }>;
  error?: string;
};

const makeCrud = <Row extends { id: string }>(path: 'mlf' | 'dlf') => ({
  list: async (params: ListParams = {}): Promise<ListResponse<Row>> => {
    try {
      const qs = new URLSearchParams();
      if (params.search) qs.set('search', params.search);
      qs.set('page', String(params.page ?? 1));
      qs.set('limit', String(params.limit ?? 25));
      const res = await fetch(`${API_BASE_URL}/api/loss-factors/${path}/list?${qs.toString()}`, { headers });
      return (await res.json()) as ListResponse<Row>;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to load' };
    }
  },

  create: async (body: Partial<Row>): Promise<MutateResponse<Row>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loss-factors/${path}`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      return (await res.json()) as MutateResponse<Row>;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to create' };
    }
  },

  update: async (id: string, body: Partial<Row>): Promise<MutateResponse<Row>> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loss-factors/${path}/${id}`, {
        method: 'PATCH', headers, body: JSON.stringify(body),
      });
      return (await res.json()) as MutateResponse<Row>;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to update' };
    }
  },

  remove: async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loss-factors/${path}/${id}`, {
        method: 'DELETE', headers,
      });
      return (await res.json()) as { success: boolean; error?: string };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Failed to delete' };
    }
  },

  /** Download the full table as CSV. Columns match the bulk-upload format,
   *  so an export can be edited and re-uploaded. */
  exportCsv: async (): Promise<{ success: boolean; error?: string }> => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/loss-factors/${path}/export`, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        return { success: false, error: (body as { error?: string }).error ?? `Export failed (${res.status})` };
      }
      const blob = await res.blob();
      const match = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] ?? `${path}_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      return { success: true };
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Export failed' };
    }
  },

  bulkUpload: async (file: File, mode: BulkMode = 'replace'): Promise<BulkUploadResponse> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mode', mode);
      const res = await fetch(`${API_BASE_URL}/api/loss-factors/${path}/bulk`, {
        method: 'POST',
        headers: { 'x-api-key': 'scopingai' },
        body: formData,
      });
      return (await res.json()) as BulkUploadResponse;
    } catch (error: unknown) {
      return { success: false, error: error instanceof Error ? error.message : 'Upload failed' };
    }
  },
});

export const mlfAdminService = makeCrud<MlfRow>('mlf');
export const dlfAdminService = makeCrud<DlfRow>('dlf');
